/**
 * Study Flow Configuration
 *
 * This file defines the flow steps and routing logic for different study types.
 * Each study type can have its own unique flow, making it easy to add new types in the future.
 */
export type StudyType = "asset" | "scene";

export type StudyStep = "study-select" | "study-upload" | "study-review" | "study-context" | "study-process" | "reports";

/**
 * Flow step definition
 */
export interface FlowStep {
  // Unique identifier for this step
  id: StudyStep;
  // Display label for the flow UI
  label: string;
  // Route path segment (e.g., "study-select")
  route: string;
  // Optional: Skip this step in the flow
  skip?: boolean;
}

/**
 * Complete flow configuration for a study type
 */
export interface StudyFlowConfig {
  // Type of study this flow applies to
  type: StudyType;
  // Ordered array of steps in this flow
  steps: FlowStep[];
  // Optional: Custom navigation overrides
  customNavigationRules?: {
    [key: string]: (projectId: string, studyId: string, context?: NavigationContext) => string;
  };
}

/**
 * Context passed to custom navigation rules
 */
export interface NavigationContext {
  hasMemoryEmotion?: boolean;
}

/**
 * Asset Study Flow Configuration
 */
const assetFlowConfig: StudyFlowConfig = {
  type: "asset",
  steps: [
    { id: "study-select", label: "Select assets", route: "study-select" },
    { id: "study-upload", label: "Upload", route: "study-upload" },
    { id: "study-review", label: "Review", route: "study-review" },
    { id: "study-context", label: "Context", route: "study-context" },
    { id: "study-process", label: "Process", route: "study-process" },
    { id: "reports", label: "Reports", route: "reports/overview" },
  ],
  customNavigationRules: {
    "study-process": (projectId: string, studyId: string, context?: NavigationContext) => {
      // If user has Memory & Emotion access, go to creative impact overview
      // Otherwise, go to attention overview
      const hasMemoryEmotion = context?.hasMemoryEmotion ?? false;
      const reportRoute = hasMemoryEmotion ? "reports/overview" : "reports/attention/overview";
      return `/projects/${projectId}/studies/${studyId}/asset/${reportRoute}`;
    },
  },
};

/**
 * Scene Study Flow Configuration
 */
const sceneFlowConfig: StudyFlowConfig = {
  type: "scene",
  steps: [
    { id: "study-select", label: "Select scenes", route: "study-select" },
    { id: "study-upload", label: "Upload", route: "study-upload" },
    { id: "study-review", label: "Review", route: "study-review" },
    { id: "study-process", label: "Process", route: "study-process" },
    { id: "reports", label: "Reports", route: "reports/overview" },
  ],
};

/**
 * Registry of all study flow configurations
 * Any new study types should be added here
 */
export const STUDY_FLOW_REGISTRY: Record<StudyType, StudyFlowConfig> = {
  asset: assetFlowConfig,
  scene: sceneFlowConfig,
};

/**
 * Get the flow configuration for a specific study type
 */
export const getFlowConfig = (studyType: StudyType = "asset"): StudyFlowConfig => {
  return STUDY_FLOW_REGISTRY[studyType] || STUDY_FLOW_REGISTRY.asset;
};

/**
 * Get all steps for a study type
 */
export const getFlowSteps = (studyType: StudyType = "asset"): FlowStep[] => {
  const config = getFlowConfig(studyType);
  return config.steps.filter((step) => !step.skip);
};

/**
 * Check if a step exists in a flow
 */
export const hasStep = (studyType: StudyType = "asset", stepId: StudyStep): boolean => {
  const steps = getFlowSteps(studyType);
  return steps.some((step) => step.id === stepId);
};

/**
 * Get the next step in the flow
 */
export const getNextStep = (studyType: StudyType = "asset", currentStep: StudyStep): FlowStep | null => {
  const steps = getFlowSteps(studyType);
  const currentIndex = steps.findIndex((step) => step.id === currentStep);

  if (currentIndex === -1 || currentIndex === steps.length - 1) {
    return null;
  }

  return steps[currentIndex + 1];
};

/**
 * Get the previous step in the flow
 */
export const getPreviousStep = (studyType: StudyType = "asset", currentStep: StudyStep): FlowStep | null => {
  const steps = getFlowSteps(studyType);
  const currentIndex = steps.findIndex((step) => step.id === currentStep);

  if (currentIndex <= 0) {
    return null;
  }

  return steps[currentIndex - 1];
};

/**
 * Build a full route path for a step
 * studyType is included in the path but may be need to be omitted in the future
 */
export const buildStepRoute = (projectId: string, studyId: string, studyType: StudyType, step: FlowStep): string => {
  return `/projects/${projectId}/studies/${studyId}/${studyType}/${step.route}`;
};

/**
 * Get the next route in the flow
 */
export const getNextRoute = (studyType: StudyType = "asset", currentStep: StudyStep, projectId: string, studyId: string, context?: NavigationContext): string | null => {
  const config = getFlowConfig(studyType);

  // Check for custom navigation rules
  if (config.customNavigationRules?.[currentStep]) {
    return config.customNavigationRules[currentStep](projectId, studyId, context);
  }

  const nextStep = getNextStep(studyType, currentStep);
  if (!nextStep) return null;

  return buildStepRoute(projectId, studyId, studyType, nextStep);
};

/**
 * Check if current step is completed (helper for flow UI)
 */
export const isStepCompleted = (studyType: StudyType = "asset", currentStep: StudyStep, targetStep: StudyStep): boolean => {
  // If we're at reports, all steps are completed
  if (currentStep === "reports") {
    return true;
  }

  const steps = getFlowSteps(studyType);
  const currentIndex = steps.findIndex((step) => step.id === currentStep);
  const targetIndex = steps.findIndex((step) => step.id === targetStep);

  return currentIndex > targetIndex;
};
